// src/components/snapshot/strategy-brief/StrategyBriefQuadrant.tsx

import { useMemo, useState } from 'react';

export type StrategyBriefQuadrantPoint = {
  id: string;
  label: string;
  x: number;
  y: number;
  category?: 'Quick Wins' | 'Strategic Investments' | 'Efficiency Plays' | 'Resource Drains';
};

type Props = {
  xLabel: string;
  yLabel: string;
  points: StrategyBriefQuadrantPoint[];
  keyInsight: string;
};

const categoryColor = (category?: StrategyBriefQuadrantPoint['category']) => {
  switch (category) {
    case 'Quick Wins':
      return { dot: '#FF6E4C', ring: 'rgba(255,110,76,0.25)' };
    case 'Strategic Investments':
      return { dot: '#18365E', ring: 'rgba(24,54,94,0.20)' };
    case 'Efficiency Plays':
      return { dot: '#10B981', ring: 'rgba(16,185,129,0.20)' };
    case 'Resource Drains':
      return { dot: '#F59E0B', ring: 'rgba(245,158,11,0.20)' };
    default:
      return { dot: '#64748B', ring: 'rgba(100,116,139,0.18)' };
  }
};

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

export function StrategyBriefQuadrant({ xLabel, yLabel, points, keyInsight }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const active = useMemo(() => points.find((p) => p.id === activeId) ?? null, [activeId, points]);

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white shadow-sm p-6 sm:p-7 break-inside-avoid">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-wide uppercase text-[#717182]">
              Strategy Position Visual
            </div>
            <div className="text-base font-extrabold text-[#18365E] mt-1">
              Value vs. Readiness Distribution
            </div>
            <div className="text-xs text-[#717182] mt-1">
              Hover or select an initiative to see details.
            </div>
          </div>

          {active && (
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3">
              <div className="text-[11px] font-semibold text-[#18365E]">{active.label}</div>
              <div className="text-[11px] text-[#717182] mt-0.5">
                {yLabel}: <span className="font-semibold text-[#18365E]">{active.y}</span>
                <span className="mx-2 text-[#CBD5E1]">|</span>
                {xLabel}: <span className="font-semibold text-[#18365E]">{active.x}</span>
              </div>
            </div>
          )}
        </div>

        <div className="relative rounded-2xl border border-[#E5E7EB] bg-gradient-to-b from-white to-[#F5F7FA] overflow-hidden break-inside-avoid">
          <div className="relative h-[360px] sm:h-[420px]">
            <div className="absolute inset-0">
              <div className="absolute left-1/2 top-0 h-full w-px bg-[#E5E7EB]" />
              <div className="absolute left-0 top-1/2 w-full h-px bg-[#E5E7EB]" />

              {/* Quadrant corner labels */}
              <div className="absolute left-5 top-4 text-[11px] font-semibold text-[#717182]">
                Strategic Investments
              </div>
              <div className="absolute right-5 top-4 text-[11px] font-semibold text-[#717182]">
                Quick Wins
              </div>
              <div className="absolute left-5 bottom-4 text-[11px] font-semibold text-[#717182]">
                Resource Drains
              </div>
              <div className="absolute right-5 bottom-4 text-[11px] font-semibold text-[#717182]">
                Efficiency Plays
              </div>

              {/* Axis labels */}
              <div className="absolute left-4 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[10px] font-semibold tracking-wide uppercase text-[#717182]">
                {yLabel}
              </div>
              <div className="absolute left-1/2 bottom-3 -translate-x-1/2 text-[10px] font-semibold tracking-wide uppercase text-[#717182]">
                {xLabel}
              </div>

              {/* Insight overlays */}
              <div className="absolute left-[56%] top-[14%]">
                <div className="rounded-xl bg-white/90 border border-[#E5E7EB] px-3 py-2 shadow-sm">
                  <div className="text-[11px] font-semibold text-[#18365E]">High-value initiatives not prioritized</div>
                  <div className="text-[10px] text-[#717182] mt-0.5">Value clusters without consistent portfolio advancement</div>
                </div>
                <div className="h-6 w-px bg-[#CBD5E1] ml-4" />
              </div>

              <div className="absolute left-[10%] bottom-[10%]">
                <div className="rounded-xl bg-white/90 border border-[#E5E7EB] px-3 py-2 shadow-sm">
                  <div className="text-[11px] font-semibold text-[#18365E]">Execution-heavy, low-return cluster</div>
                  <div className="text-[10px] text-[#717182] mt-0.5">Readiness drag signals capacity and governance friction</div>
                </div>
              </div>
            </div>

            {points.map((p) => {
              const xPct = clamp01(p.x / 100);
              const yPct = clamp01(p.y / 100);
              const left = `${xPct * 100}%`;
              const top = `${(1 - yPct) * 100}%`;

              const isActive = p.id === activeId;
              const colors = categoryColor(p.category);

              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActiveId((prev) => (prev === p.id ? null : p.id))}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group"
                  style={{ left, top }}
                  aria-label={p.label}
                >
                  <div
                    className="relative"
                    style={{ width: 14, height: 14 }}
                  >
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        backgroundColor: colors.dot,
                        boxShadow: isActive
                          ? `0 0 0 5px ${colors.ring}, 0 10px 24px rgba(15,23,42,0.18)`
                          : `0 0 0 4px ${colors.ring}`,
                      }}
                    />
                  </div>

                  <div className="pointer-events-none absolute left-1/2 top-[-10px] -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="whitespace-nowrap rounded-lg bg-[#0B1220] px-2.5 py-1.5 text-[10px] font-semibold text-white shadow-lg">
                      {p.label}
                      {p.category ? <span className="text-white/70 font-medium"> · {p.category}</span> : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] px-4 py-3">
          <div className="text-sm font-semibold text-[#18365E]">{keyInsight}</div>
        </div>
      </div>
    </section>
  );
}
